import React, { useEffect, useState } from 'react';
import axios from 'axios';
import InviteUserModal from '../../components/InviteUserModal';

/**
 * Users administration screen — the table of all users + the "invite"
 * button. Per-user edits happen on a dedicated profile page; clicking
 * a row navigates there via the ``onOpenProfile`` callback the parent
 * route provides.
 *
 * One component, two mount points:
 *   - Customer side: ``usersBase = "/api/customer/users"``
 *   - Admin side:    ``usersBase = "/api/admin/customers/<cid>/users"``
 */
function UsersPage({
  usersBase,
  currentUserId = null,
  allowManagerCreation = false,
  onOpenProfile,
}) {
  const [users, setUsers] = useState(null);
  const [error, setError] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [busyUserId, setBusyUserId] = useState(null);
  const [toast, setToast] = useState(null);

  const templatesEndpoint = usersBase.replace(/\/users$/, '') + '/user-templates';

  const load = () => {
    axios.get(usersBase)
      .then(res => setUsers(res.data))
      .catch(err => setError(err.response?.data?.error || 'שגיאה בטעינת המשתמשים'));
  };
  useEffect(load, [usersBase]);

  const flash = (text) => {
    setToast(text);
    setTimeout(() => setToast(null), 2400);
  };

  const onDeactivate = async (u) => {
    if (!window.confirm(`להשבית את ${u.name}? המשתמש לא יוכל להתחבר עד שיופעל מחדש.`)) return;
    setBusyUserId(u.id);
    try {
      await axios.post(`${usersBase}/${u.id}/deactivate`);
      flash('המשתמש הושבת');
      load();
    } catch (err) {
      alert(err.response?.data?.error || 'שגיאה בהשבתה');
    } finally {
      setBusyUserId(null);
    }
  };

  const onReactivate = async (u) => {
    setBusyUserId(u.id);
    try {
      await axios.post(`${usersBase}/${u.id}/reactivate`);
      flash('המשתמש הופעל מחדש');
      load();
    } catch (err) {
      alert(err.response?.data?.error || 'שגיאה בהפעלה מחדש');
    } finally {
      setBusyUserId(null);
    }
  };

  const onResendInvite = async (u) => {
    setBusyUserId(u.id);
    try {
      await axios.post(`${usersBase}/${u.id}/resend-invite`);
      flash('הזמנה חדשה נשלחה במייל');
    } catch (err) {
      alert(err.response?.data?.error || 'שגיאה בשליחת ההזמנה');
    } finally {
      setBusyUserId(null);
    }
  };

  if (users === null && !error) return <div className="loading">טוען...</div>;

  return (
    <>
      <div className="section-header">
        <h3>משתמשים</h3>
        <button className="btn btn-primary btn-sm" onClick={() => setShowCreate(true)}>
          + משתמש חדש
        </button>
      </div>

      <p style={{ color: '#6b7280', fontSize: '0.85rem', marginTop: '-0.5rem', marginBottom: '1rem' }}>
        כל משתמש שתוסיפו כאן יקבל מייל עם קישור לקביעת סיסמה והתחברות. הקישור תקף ל-7 ימים.
      </p>

      {error && (
        <div style={{
          background: '#fef2f2', border: '1px solid #fecaca',
          color: '#b91c1c', padding: '0.6rem 0.85rem',
          borderRadius: '6px', marginBottom: '0.85rem', fontSize: '0.85rem',
        }}>
          {error}
        </div>
      )}

      <div className="data-table">
        <table>
          <thead>
            <tr>
              <th>שם</th>
              <th>אימייל</th>
              <th>תפקיד</th>
              <th>כניסה אחרונה</th>
              <th>סטטוס</th>
              <th style={{ textAlign: 'left' }}>פעולות</th>
            </tr>
          </thead>
          <tbody>
            {(users || []).map(u => {
              const isSelf = currentUserId === u.id;
              const inactive = !u.is_active;
              const invitedNeverLoggedIn = !inactive && !u.has_logged_in;
              const status = inactive
                ? { text: 'מושבת', bg: '#fef2f2', fg: '#b91c1c' }
                : (invitedNeverLoggedIn
                  ? { text: 'הוזמן — טרם נכנס', bg: '#fffbeb', fg: '#92400e' }
                  : { text: 'פעיל', bg: '#ecfdf5', fg: '#047857' });
              const roleLabel = u.template_label
                || (u.role === 'manager' ? 'מנהל' : 'צופה');
              return (
                <tr
                  key={u.id}
                  style={{
                    ...(inactive ? { opacity: 0.55 } : {}),
                    cursor: onOpenProfile ? 'pointer' : 'default',
                  }}
                  onClick={() => onOpenProfile && onOpenProfile(u)}
                >
                  <td>
                    {u.name}
                    {isSelf && (
                      <span style={{ marginRight: '0.4rem', color: '#9ca3af', fontSize: '0.75rem' }}>
                        (אתה)
                      </span>
                    )}
                  </td>
                  <td>{u.email || '-'}</td>
                  <td>
                    {roleLabel}
                    {u.template_customized && (
                      <span style={{ marginRight: '0.35rem', color: '#9ca3af', fontSize: '0.72rem' }}>
                        • הותאם
                      </span>
                    )}
                  </td>
                  <td>
                    {u.last_login_at
                      ? new Date(u.last_login_at).toLocaleString('he-IL', {
                          timeZone: 'Asia/Jerusalem',
                          year: 'numeric', month: '2-digit', day: '2-digit',
                          hour: '2-digit', minute: '2-digit',
                        })
                      : <span style={{ color: '#9ca3af' }}>טרם נכנס</span>}
                  </td>
                  <td>
                    <span style={{
                      display: 'inline-block',
                      padding: '0.15rem 0.6rem',
                      borderRadius: '999px',
                      fontSize: '0.72rem',
                      fontWeight: 500,
                      background: status.bg,
                      color: status.fg,
                    }}>
                      {status.text}
                    </span>
                  </td>
                  <td style={{ textAlign: 'left' }} onClick={(e) => e.stopPropagation()}>
                    <RowActions
                      user={u}
                      isSelf={isSelf}
                      busy={busyUserId === u.id}
                      onOpen={() => onOpenProfile && onOpenProfile(u)}
                      onDeactivate={() => onDeactivate(u)}
                      onReactivate={() => onReactivate(u)}
                      onResendInvite={() => onResendInvite(u)}
                    />
                  </td>
                </tr>
              );
            })}
            {users && users.length === 0 && (
              <tr>
                <td colSpan="6" style={{ textAlign: 'center', color: '#9ca3af', padding: '2rem' }}>
                  אין עדיין משתמשים. הזמינו אחד עם הכפתור למעלה.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {showCreate && (
        <InviteUserModal
          usersBase={usersBase}
          templatesEndpoint={templatesEndpoint}
          allowManagerCreation={allowManagerCreation}
          onClose={() => setShowCreate(false)}
          onCreated={(_user) => {
            setShowCreate(false);
            flash('נשלחה הזמנה במייל');
            load();
          }}
        />
      )}

      {toast && (
        <div style={{
          position: 'fixed', bottom: '1.5rem', left: '50%',
          transform: 'translateX(-50%)',
          background: '#1a1a2e', color: '#fff',
          padding: '0.6rem 1.1rem', borderRadius: '6px',
          fontSize: '0.85rem', zIndex: 200,
          boxShadow: '0 6px 20px rgba(0,0,0,0.2)',
        }}>
          {toast}
        </div>
      )}
    </>
  );
}


function RowActions({ user, isSelf, busy, onOpen, onDeactivate, onReactivate, onResendInvite }) {
  const baseBtn = {
    padding: '0.25rem 0.6rem',
    border: '1px solid #e5e7eb',
    background: '#fff',
    color: '#1a1a2e',
    borderRadius: '5px',
    fontSize: '0.78rem',
    cursor: busy ? 'wait' : 'pointer',
    fontFamily: 'Heebo, sans-serif',
    marginInlineStart: '0.35rem',
  };
  const dangerBtn = { ...baseBtn, background: '#fef2f2', color: '#b91c1c', borderColor: '#fecaca' };

  return (
    <span>
      <button style={baseBtn} disabled={busy} onClick={onOpen}>פתח כרטיס</button>
      {!user.is_active ? (
        <button style={baseBtn} disabled={busy} onClick={onReactivate}>הפעל מחדש</button>
      ) : (
        <>
          {!user.has_logged_in && (
            <button style={baseBtn} disabled={busy} onClick={onResendInvite}>
              שלח שוב הזמנה
            </button>
          )}
          {!isSelf && (
            <button style={dangerBtn} disabled={busy} onClick={onDeactivate}>
              השבת
            </button>
          )}
        </>
      )}
    </span>
  );
}


export default UsersPage;
